import PhotoSwipeLightbox from 'photoswipe/lightbox';
import PhotoSwipe from 'photoswipe';
import 'photoswipe/style.css';
import { initCollectionOrderPopup } from './components/collection-order-popup';

// ============================================================================
// VARIATION URL PARAMETER FUNCTIONS
// ============================================================================

/**
 * Get current variation parameters from URL
 * @returns {Object} Object with attribute names as keys and values as values
 */
function getVariationParamsFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const variationParams = {};
    
    // Get all variation parameters from URL (without attribute_ prefix)
    for (const [key, value] of urlParams.entries()) {
        // Check if this is a variation parameter by looking for corresponding select element
        const select = document.querySelector(`select[name="attribute_${key}"]`);
        if (select) {
            variationParams[key] = value;
        }
    }
    
    return variationParams;
}

/**
 * Update URL with current variation parameters
 */
function updateURLWithVariations() {
    const hiddenSelects = document.querySelectorAll('.chorna-variations select[style*="display: none"]');
    const urlParams = new URLSearchParams(window.location.search);
    
    // Remove existing variation parameters (check by looking for corresponding selects)
    const keysToRemove = [];
    for (const [key] of urlParams.entries()) {
        const select = document.querySelector(`select[name="attribute_${key}"]`);
        if (select) {
            keysToRemove.push(key);
        }
    }
    keysToRemove.forEach(key => urlParams.delete(key));
    
    // Add current variation parameters (without attribute_ prefix)
    hiddenSelects.forEach(select => {
        if (select.value) {
            const attributeName = select.name.replace('attribute_', '');
            urlParams.set(attributeName, select.value);
        }
    });
    
    // Update URL without page reload
    const newURL = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
    window.history.replaceState({}, '', newURL);
}

/**
 * Set default variations (first option for each attribute)
 */
function setDefaultVariations() {
    const hiddenSelects = document.querySelectorAll('.chorna-variations select[style*="display: none"]');
    let hasSetDefaults = false;
    
    hiddenSelects.forEach(select => {
        // Only set default if no value is currently selected
        if (!select.value && select.options.length > 1) {
            // Skip the first option (usually "Choose an option")
            const firstValueOption = select.options[1];
            if (firstValueOption && firstValueOption.value) {
                select.value = firstValueOption.value;
                hasSetDefaults = true;
                
                // Trigger change event to update WooCommerce
                const changeEvent = new Event('change', { bubbles: true });
                select.dispatchEvent(changeEvent);
            }
        }
    });
    
    return hasSetDefaults;
}

/**
 * Restore variations from URL parameters
 * @returns {boolean} True if variations were restored, false otherwise
 */
function restoreVariationsFromURL() {
    const variationParams = getVariationParamsFromURL();
    
    if (Object.keys(variationParams).length === 0) {
        return false; // No parameters to restore
    }
    
    let hasRestored = false;
    
    // Set hidden select values from URL parameters
    Object.entries(variationParams).forEach(([attributeName, value]) => {
        const select = document.querySelector(`select[name="attribute_${attributeName}"]`);
        if (select && value) {
            select.value = value;
            hasRestored = true;
            
            // Trigger change event to update WooCommerce
            const changeEvent = new Event('change', { bubbles: true });
            select.dispatchEvent(changeEvent);
        }
    });
    
    return hasRestored;
}

/**
 * Sync button states with select values
 */
function syncButtonStates() {
    const hiddenSelects = document.querySelectorAll('.chorna-variations select[style*="display: none"]');
    
    hiddenSelects.forEach(select => {
        const attribute = select.name.replace('attribute_', '');
        const selectedValue = select.value;
        
        // Remove selected class from all buttons in this attribute group
        const allButtonsInGroup = document.querySelectorAll(`[data-attribute="${attribute}"]`);
        allButtonsInGroup.forEach(btn => btn.classList.remove('selected'));
        
        // Add selected class to the button matching the select value
        if (selectedValue) {
            const selectedButton = document.querySelector(`[data-attribute="${attribute}"][data-value="${selectedValue}"]`);
            if (selectedButton) {
                selectedButton.classList.add('selected');
            }
        }
    });
}

// ============================================================================
// GALLERY FUNCTIONS
// ============================================================================

/**
 * Get current visual attributes from form
 * @returns {Object|null} Object with visual attributes or null
 */
function getCurrentVisualAttributes() {
    const variationForm = document.querySelector('.variations_form');
    if (!variationForm) {
        return null;
    }
    
    const visualAttributeNames = window.chornaVisualAttributes.visualAttributes;;
    const visualAttributes = {};
    
    // Check all select elements for visual attributes
    const selects = variationForm.querySelectorAll('select');
    
    selects.forEach(select => {
        const name = select.name;
        
        if (name && name.startsWith('attribute_')) {
            // Remove 'attribute_' prefix
            let attrName = name.replace('attribute_', '');
            
            // For global attributes (pa_ prefix), remove it
            // For custom attributes, keep as is
            if (attrName.startsWith('pa_')) {
                attrName = attrName.replace('pa_', '');
            }
            
            attrName = attrName.toLowerCase();
            
            if (visualAttributeNames.includes(attrName) && select.value) {
                visualAttributes[attrName] = select.value;
            }
        }
    });

    return Object.keys(visualAttributes).length > 0 ? visualAttributes : null;
}

/**
 * Check if visual attributes have changed
 * @param {Object} current Current visual attributes
 * @param {Object} previous Previous visual attributes
 * @returns {boolean} True if changed, false otherwise
 */
function hasVisualAttributesChanged(current, previous) {
    if (!current && !previous) return false;
    if (!current || !previous) return true;
    
    const currentKeys = Object.keys(current).sort();
    const previousKeys = Object.keys(previous).sort();
    
    if (currentKeys.length !== previousKeys.length) return true;
    
    for (let i = 0; i < currentKeys.length; i++) {
        if (currentKeys[i] !== previousKeys[i] || current[currentKeys[i]] !== previous[currentKeys[i]]) {
            return true;
        }
    }
    
    return false;
}

/**
 * Handle variation change for gallery switching
 * @param {number} variationId The variation ID
 * @param {Object} variationGalleries The variation galleries data
 * @param {Object} mainSwiper The main swiper instance
 */
function handleVariationChange(variationId, variationGalleries, mainSwiper) {
    if (!variationId) {
        return;
    }

    // Get current visual attributes to determine which group to show
    const currentVisualAttributes = getCurrentVisualAttributes();
    if (!currentVisualAttributes) {
        return;
    }

    // Find the matching visual group
    let targetGroupKey = null;
    for (const [groupKey, groupData] of Object.entries(variationGalleries)) {
        if (groupKey === '_variation_to_group') continue; // Skip mapping object
        
        if (groupData.visual_attributes) {
            // Compare visual attributes
            const groupAttrs = groupData.visual_attributes;
            const currentKeys = Object.keys(currentVisualAttributes).sort();
            const groupKeys = Object.keys(groupAttrs).sort();
            
            if (currentKeys.length === groupKeys.length) {
                let matches = true;
                for (const key of currentKeys) {
                    if (currentVisualAttributes[key] !== groupAttrs[key]) {
                        matches = false;
                        break;
                    }
                }
                if (matches) {
                    targetGroupKey = groupKey;
                    break;
                }
            }
        }
    }

    if (targetGroupKey && variationGalleries[targetGroupKey]) {
        const groupData = variationGalleries[targetGroupKey];
        const startIndex = groupData.start_index;
        
        // Scroll to the visual group gallery start
        if (mainSwiper && typeof startIndex !== 'undefined') {
            mainSwiper.slideTo(startIndex, 500); // 500ms animation
        }
    }
}

// ============================================================================
// INITIALIZATION FUNCTIONS
// ============================================================================

/**
 * Initialize button variations functionality
 */
function initButtonVariations() {
    // Handle custom variation buttons
    const variationButtons = document.querySelectorAll('.variation-option');
    
    if (variationButtons.length > 0) {
        variationButtons.forEach(button => {
            button.addEventListener('click', function(e) {
                e.preventDefault();
                
                const attribute = this.getAttribute('data-attribute');
                const value = this.getAttribute('data-value');
                
                // Remove selected class from all buttons in this attribute group
                const allButtonsInGroup = document.querySelectorAll(`[data-attribute="${attribute}"]`);
                allButtonsInGroup.forEach(btn => btn.classList.remove('selected'));
                
                // Add selected class to clicked button
                this.classList.add('selected');
                
                // Update hidden select element for WooCommerce compatibility
                const hiddenSelect = document.querySelector(`select[name="attribute_${attribute}"]`);
                if (hiddenSelect) {
                    hiddenSelect.value = value;
                    
                    // Update URL with new variation parameters
                    updateURLWithVariations();
                    
                    // Trigger change event on the select element
                    const changeEvent = new Event('change', { bubbles: true });
                    hiddenSelect.dispatchEvent(changeEvent);
                }
            });
        });

        // Listen for WooCommerce variation changes to sync button states
        document.addEventListener('found_variation', function(event) {
            syncButtonStates();
        });

        document.addEventListener('reset_data', function(event) {
            syncButtonStates();
        });

        // Try to restore variations from URL parameters first
        const restored = restoreVariationsFromURL();
        
        // If no URL parameters were restored, set default variations (first option for each attribute)
        if (!restored) {
            setDefaultVariations();
        }
        
        // Initial sync on page load
        syncButtonStates();
        
        // If we restored from URL or set defaults, trigger a variation update
        if (restored || !restored) {
            // Small delay to ensure WooCommerce is ready
            setTimeout(() => {
                const variationForm = document.querySelector('.variations_form');
                if (variationForm) {
                    variationForm.dispatchEvent(new Event('woocommerce_update_variation_values'));
                }
            }, 200);
        }
    }
}

function initPhotoSwipeLightbox() {
    const lightbox = new PhotoSwipeLightbox({
        gallery: '.chorna-product-gallery',
        children: '.gallery-main-swiper .swiper-slide a',
        pswpModule: PhotoSwipe,
        showHideAnimationType: 'fade',
        bgOpacity: 0.8
    });

    lightbox.init();
}

/**
 * Lazy-load source, init Plyr, and play muted gallery video.
 *
 * @param {HTMLElement} slide Video swiper slide.
 */
function activateGalleryVideo(slide) {
    if (!slide || typeof Plyr === 'undefined') {
        return;
    }

    const playerWrap = slide.querySelector('.gallery-video-player');
    const video = slide.querySelector('.gallery-video-el');

    if (!playerWrap || !video) {
        return;
    }

    if (video.dataset.srcLoaded !== '1') {
        const source = document.createElement('source');
        source.src = playerWrap.dataset.videoSrc;
        source.type = playerWrap.dataset.videoType || 'video/mp4';
        video.appendChild(source);
        video.load();
        video.dataset.srcLoaded = '1';
    }

    if (!playerWrap.plyrInstance) {
        playerWrap.plyrInstance = new Plyr(video, {
            autoplay: true,
            muted: true,
            volume: 0,
            controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen'],
            hideControls: true,
            clickToPlay: true,
            ratio: null,
        });

        playerWrap.plyrInstance.on('playing', function() {
            playerWrap.classList.add('is-playing');
        });

        playerWrap.plyrInstance.on('pause ended', function() {
            if (playerWrap.plyrInstance && playerWrap.plyrInstance.currentTime === 0) {
                playerWrap.classList.remove('is-playing');
            }
        });
    }

    const player = playerWrap.plyrInstance;
    player.muted = true;
    player.volume = 0;

    const playPromise = player.play();
    if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function() {
            // Autoplay can be blocked; cover stays visible until user interaction.
        });
    }
}

/**
 * Pause all gallery video players and show covers again when fully stopped at start.
 *
 * @param {HTMLElement} galleryContainer Gallery root.
 */
function pauseGalleryVideos(galleryContainer) {
    galleryContainer.querySelectorAll('.gallery-video-player').forEach(function(playerWrap) {
        if (playerWrap.plyrInstance) {
            playerWrap.plyrInstance.pause();
        }
        playerWrap.classList.remove('is-playing');
    });
}

/**
 * Initialize product gallery functionality
 */
function initProductGallery() {
    const galleryContainer = document.querySelector('.chorna-product-gallery');
    
    if (!galleryContainer) {
        return;
    }

    // Initialize PhotoSwipe lightbox
    initPhotoSwipeLightbox();

    let variationGalleries = {};
    let mainSwiper = null;
    let thumbsSwiper = null;
    const hasGalleryVideo = galleryContainer.dataset.hasVideo === '1';

    // Parse variation galleries data
    const variationGalleriesData = galleryContainer.getAttribute('data-variation-galleries');
    if (variationGalleriesData) {
        try {
            variationGalleries = JSON.parse(variationGalleriesData);
        } catch (e) {
            console.warn('Failed to parse variation galleries data:', e);
        }
    }

    let thumbsSliderAttributes = {
        spaceBetween: 5,
        slideHeight: 100,
        slidesPerView: 'auto',
        direction: "horizontal",
        freeMode: true,
        watchSlidesProgress: true,
        mousewheel: true,
        watchOverflow: true,
        navigation: {
            nextEl: '.gallery-thumbs-container .swiper-button-next',
            prevEl: '.gallery-thumbs-container .swiper-button-prev',
        },
        breakpoints: {
            992: {
                direction: "vertical",
            }
        }
    };

    // Initialize thumbs Swiper first
    thumbsSwiper = new Swiper('.gallery-thumbs-swiper', thumbsSliderAttributes);

    // Initialize main Swiper with thumbs connection.
    // Disable loop when a video slide is present to avoid duplicated players.
    mainSwiper = new Swiper('.gallery-main-swiper', {
        spaceBetween: 0,
        slidesPerView: 1,
        autoHeight: true,
        watchOverflow: true,
        loop: !hasGalleryVideo,
        navigation: {
            nextEl: '.gallery-main-swiper .swiper-button-next',
            prevEl: '.gallery-main-swiper .swiper-button-prev',
        },
        thumbs: {
            swiper: thumbsSwiper
        },
    });

    if (hasGalleryVideo) {
        const syncVideoPlayback = function() {
            pauseGalleryVideos(galleryContainer);

            const activeSlide = mainSwiper.slides[mainSwiper.activeIndex];
            if (activeSlide && activeSlide.classList.contains('gallery-video-slide')) {
                activateGalleryVideo(activeSlide);
            }
        };

        mainSwiper.on('slideChangeTransitionEnd', syncVideoPlayback);

        // If video is the only/first slide.
        if (mainSwiper.slides[mainSwiper.activeIndex]?.classList.contains('gallery-video-slide')) {
            syncVideoPlayback();
        }
    }

    // Track previous visual attributes to detect changes
    let previousVisualAttributes = null;

    // Listen for WooCommerce variation changes
    document.addEventListener('found_variation', function(event) {
        const variationId = event.detail.variation_id;
        
        const currentVisualAttributes = getCurrentVisualAttributes();
        
        // Only switch gallery if visual attributes have changed
        if (hasVisualAttributesChanged(currentVisualAttributes, previousVisualAttributes)) {
            handleVariationChange(variationId, variationGalleries, mainSwiper);
            previousVisualAttributes = currentVisualAttributes;
        }
    });

    // Also listen for other possible variation events
    document.addEventListener('woocommerce_variation_select_change', function(event) {
        // Event handler for variation select changes
    });

    document.addEventListener('woocommerce_update_variation_values', function(event) {
        // Event handler for variation value updates
    });

    // Listen for variation form changes and trigger gallery switching
    const variationForm = document.querySelector('.variations_form');
    
    // Get visual attribute names from localized data
    let visualAttributeNames;
    if (!window.chornaVisualAttributes) {
        visualAttributeNames = ['material', 'metal', 'color'];
    } else {
        visualAttributeNames = window.chornaVisualAttributes.visualAttributes;
    }

    if (variationForm) {
        variationForm.addEventListener('change', function(event) {
            // Check if this is a visual attribute change
            if (event.target.name && event.target.name.startsWith('attribute_')) {
                // Remove 'attribute_' prefix
                let attrName = event.target.name.replace('attribute_', '');
                
                // For global attributes (pa_ prefix), remove it
                // For custom attributes, keep as is
                if (attrName.startsWith('pa_')) {
                    attrName = attrName.replace('pa_', '');
                }
                
                attrName = attrName.toLowerCase();

                if (visualAttributeNames.includes(attrName)) {
                    // Get current visual attributes immediately
                    const currentVisualAttributes = getCurrentVisualAttributes();
                    
                    // Only switch gallery if visual attributes have changed
                    if (hasVisualAttributesChanged(currentVisualAttributes, previousVisualAttributes)) {
                        
                        // Find the matching visual group directly
                        let targetGroupKey = null;
                        for (const [groupKey, groupData] of Object.entries(variationGalleries)) {
                            if (groupKey === '_variation_to_group') continue; // Skip mapping object
                            
                            if (groupData.visual_attributes) {
                                // Compare visual attributes
                                const groupAttrs = groupData.visual_attributes;
                                const currentKeys = Object.keys(currentVisualAttributes).sort();
                                const groupKeys = Object.keys(groupAttrs).sort();
                                
                                if (currentKeys.length === groupKeys.length) {
                                    let matches = true;
                                    for (const key of currentKeys) {
                                        if (currentVisualAttributes[key] !== groupAttrs[key]) {
                                            matches = false;
                                            break;
                                        }
                                    }
                                    if (matches) {
                                        targetGroupKey = groupKey;
                                        break;
                                    }
                                }
                            }
                        }

                        if (targetGroupKey && variationGalleries[targetGroupKey]) {
                            const groupData = variationGalleries[targetGroupKey];
                            const startIndex = groupData.start_index;
                            
                            // Scroll to the visual group gallery start
                            if (mainSwiper && typeof startIndex !== 'undefined') {
                                mainSwiper.slideTo(startIndex, 500); // 500ms animation
                            }
                        }
                        
                        previousVisualAttributes = currentVisualAttributes;
                    }
                }
            }
        });
    }
}

// ============================================================================
// MAIN INITIALIZATION
// ============================================================================

// Initialize button variations
document.addEventListener('DOMContentLoaded', function() {
    // Initialize button variations with a small delay to ensure WooCommerce is ready
    setTimeout(() => {
        initButtonVariations();
    }, 100);
});

// Initialize product gallery
document.addEventListener('DOMContentLoaded', function() {
    initProductGallery();
});

// Initialize collection order popup
document.addEventListener('DOMContentLoaded', function() {
    initCollectionOrderPopup();
});